import { useCallback, useMemo, useState } from "react";
import { useAuthorizationContext, useClientPortalConfigContext } from "../../../context";
import { ROUTES } from "../../../routes";
import { useClientPortalNavigate } from "../../../hooks/useClientPortalRouter";
import { CalendarCheck, ClipboardList, FolderOpen, List, Receipt, UsersRound } from "lucide-react";
import { useBookingAccess } from "../../../hooks/useBookingAccess";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import { getBookAGroupBookingLabel, getGroupBookingDescription } from "../../../utils/groupBookingLabel";

export const useClientPortalHomeActions = () => {
    const { clientPortalConfig } = useClientPortalConfigContext();
    const { loggedIn } = useAuthorizationContext();
    const navigateTo = useClientPortalNavigate();
    const { canBookAppointments, canBookClasses } = useBookingAccess();

    const [showLoginDialog, setShowLoginDialog] = useState(false);
    const [returnUrl, setReturnUrl] = useState<string>("");

    const shouldLoginFirst = !loggedIn && (clientPortalConfig?.areUsersRequiredToLoginFirst ?? true);
    const newUsersCanRegister = clientPortalConfig?.newUsersCanRegister ?? true;
    const shouldDisplayExistingClientsAlert = !loggedIn && !newUsersCanRegister;
    const mustLoginForBookingOrClasses =
        !loggedIn && ((clientPortalConfig?.areUsersRequiredToLoginFirst ?? true) || !newUsersCanRegister);
    const isLoggedInUserAllowedToBookAppointment =
        clientPortalConfig?.userDetails?.isLoggedInUserAllowedToBookAppointment ?? true;

    const bookingNotAllowedMessage =
        "Your profile is not currently set up to book appointments. Please contact the practice for assistance.";
    const groupBookingLabel = clientPortalConfig?.groupBookingLabel;

    const handleBookingAction = useCallback(
        (route: string) => {
            if (mustLoginForBookingOrClasses) {
                setShowLoginDialog(true);
                setReturnUrl(route);
            } else if (loggedIn && !isLoggedInUserAllowedToBookAppointment) {
                enqueueSnackbar(bookingNotAllowedMessage, { variant: "error" });
            } else {
                navigateTo(route);
            }
        },
        [mustLoginForBookingOrClasses, loggedIn, isLoggedInUserAllowedToBookAppointment, navigateTo]
    );

    const onCardClickUserLoggedInRequirement = useCallback(
        (navigateRoute: string) => {
            if (!loggedIn) {
                setShowLoginDialog(true);
                setReturnUrl(navigateRoute);
            } else {
                navigateTo(navigateRoute);
            }
        },
        [loggedIn, navigateTo]
    );

    const actions = useMemo(
        () => [
            {
                title: "Book an Appointment",
                description: "View available time slots and book an appointment.",
                icon: CalendarCheck,
                isVisible: canBookAppointments,
                onClick: () => handleBookingAction(ROUTES.APPOINTMENT_BOOKING.urlPath),
            },
            {
                title: getBookAGroupBookingLabel(groupBookingLabel),
                description: getGroupBookingDescription(groupBookingLabel),
                icon: UsersRound,
                isVisible: canBookClasses,
                onClick: () => handleBookingAction(ROUTES.CLASS_BOOKING.urlPath),
            },
            {
                title: "My Appointments",
                description: "View and manage your appointments.",
                icon: CalendarCheck,
                isVisible: clientPortalConfig.featureAccess?.canViewUpcomingAppointments ?? false,
                onClick: () => onCardClickUserLoggedInRequirement(ROUTES.APPOINTMENTS.urlPath),
            },
            {
                title: "My Invoices",
                description: "View and manage your invoices.",
                icon: Receipt,
                isVisible: clientPortalConfig.featureAccess?.canViewInvoicesList ?? false,
                onClick: () => onCardClickUserLoggedInRequirement(ROUTES.INVOICES.urlPath),
            },
            {
                title: "My Forms",
                description: "View and manage your forms.",
                icon: List,
                isVisible: clientPortalConfig.featureAccess?.canAccessFormsPage ?? false,
                onClick: () => onCardClickUserLoggedInRequirement(ROUTES.FORMS.urlPath),
            },
            {
                title: "My Documents",
                description: "View and manage shared documents.",
                icon: FolderOpen,
                isVisible: clientPortalConfig.featureAccess?.canAccessDocuments ?? false,
                onClick: () => onCardClickUserLoggedInRequirement(ROUTES.DOCUMENTS.urlPath),
            },
            {
                title: "Waitlist",
                description: "Add yourself to the waitlist.",
                icon: ClipboardList,
                isVisible: clientPortalConfig.featureAccess?.canRequestWaitlist ?? false,
                onClick: () => onCardClickUserLoggedInRequirement(ROUTES.WAITLIST_REQUEST.urlPath),
            },
        ],
        [
            clientPortalConfig.featureAccess,
            canBookAppointments,
            canBookClasses,
            onCardClickUserLoggedInRequirement,
            handleBookingAction,
            groupBookingLabel,
        ]
    );

    return {
        actions,
        showLoginDialog,
        returnUrl,
        setShowLoginDialog,
        shouldLoginFirst,
        newUsersCanRegister,
        shouldDisplayExistingClientsAlert,
    };
};
